/**
 * Audit events (spec section 17). `details` is an allowlist: ids, enums, counts, durations, status
 * codes, endpoint templates, digests. Unknown keys and free text are rejected by schema.
 */
import { z } from 'zod';
import {
  AccountIdSchema,
  AccountProviderSchema,
  AgendaIdSchema,
  AiKeyAssignmentIdSchema,
  AiKeyIdSchema,
  ArtifactIdSchema,
  AuditEventIdSchema,
  ConnectionIdSchema,
  ContentReleaseIdSchema,
  DatasetIdSchema,
  DeviceIdSchema,
  GrantIdSchema,
  InvitationIdSchema,
  LeaseIdSchema,
  NotificationIdSchema,
  ObjectTypeSchema,
  OperationIdSchema,
  OutreachBatchIdSchema,
  ProviderSchema,
  RumiSessionIdSchema,
  RumiTurnIdSchema,
  RunIdSchema,
  SessionIdSchema,
  SnapshotIdSchema,
  TemplateIdSchema,
  TemplateVersionIdSchema,
  UserIdSchema,
} from '../ids.js';
import { CapabilitySchema, RoleSchema } from '../roles.js';
import {
  ConnectionHealthSchema,
  DatasetRunStateSchema,
  RemedyActionSchema,
  SyncErrorCodeSchema,
  SyncJobStateSchema,
} from '../status.js';
import {
  ArtifactTypeSchema,
  HttpMethodSchema,
  OutreachItemStateSchema,
  OutreachModeSchema,
  OverrideScopeSchema,
  PublicationStateSchema,
  RumiModeSchema,
  RumiTurnStatusSchema,
  UsageVerificationStatusSchema,
} from '../providers/records.js';
import {
  CountSchema,
  CursorQuerySchema,
  ExportFormatSchema,
  ExportResultSchema,
  IsoDateTimeSchema,
  MicroUsdSchema,
  SemVerSchema,
  Sha256HexSchema,
  TimeRangeQuerySchema,
  defineRoute,
  pageOf,
} from './common.js';
// local-accounts change: report action enum for account telemetry events.
import { AccountReportActionSchema } from './accounts.js';
import {
  AccountOriginSchema,
  AiKeyAssignmentScopeSchema,
  AiKeyStatusSchema,
  GrantDenialReasonSchema,
  LeasePurposeSchema,
  ReleaseChannelSchema,
  RolloutStageSchema,
  TemplateTypeSchema,
  TemplateVersionStateSchema,
  UsageServiceSchema,
  UserStatusSchema,
} from './entities.js';

export const AUDIT_SCHEMA_VERSION = 1;

// ---------------------------------------------------------------------------------------------
// Actions, grouped by the event families of spec section 17
// ---------------------------------------------------------------------------------------------

export const AUDIT_ACTION_FAMILIES = {
  auth: [
    'auth.signin',
    'auth.signin_denied',
    'auth.signout',
    'auth.session_refreshed',
    'auth.entitlement_renewed',
    'auth.entitlement_denied',
    'auth.locked',
    'auth.unlocked',
  ],
  access: [
    'access.invite_created',
    'access.invite_resent',
    'access.invite_cancelled',
    'access.invite_accepted',
    'access.invite_expired',
    'access.user_activated',
    'access.user_suspended',
    'access.user_removed',
    'access.roles_changed',
    'access.account_scope_changed',
    'access.direct_send_changed',
    'access.capability_grant_changed',
    'access.session_revoked',
    'access.update_required',
  ],
  device: ['device.registered', 'device.revoked', 'device.key_rotated'],
  account: [
    'account.registered',
    'account.renamed',
    // added by server: provider type change is a distinct event (before/after in details)
    'account.provider_changed',
    'account.archived',
    'account.unarchived',
    'account.assigned',
    'account.unassigned',
    'account.deleted_local',
    // local-accounts change: local-first creation and registry telemetry.
    'account.created',
    'account.reported',
    'account.removed_local',
    'account.report_rejected',
    'connection.registered',
    'connection.replaced',
    'connection.reconnected',
    'connection.tested',
    'connection.removed',
    'connection.tenant_mismatch',
  ],
  credential: [
    'credential.revealed',
    'credential.reconnected',
    'credential.revoked',
    'credential.token_debug_viewed',
    'google.connected',
    'google.disconnected',
    'google.identity_switched',
    // real-google change: the connected Google identity differs from the signed-in Agency user
    // (warning, not blocked). Details: identityMismatch only; never the addresses.
    'google.identity_mismatch',
  ],
  ai_key: [
    'ai_key.registered',
    'ai_key.assigned',
    'ai_key.rotated',
    'ai_key.disabled',
    'ai_key.revoked',
    'ai_key.provider_revocation_confirmed',
    'ai_key.provider_revocation_failed',
    'ai_key.grant_issued',
    'ai_key.grant_redeemed',
    'ai_key.grant_denied',
    'ai_key.grant_expired',
  ],
  sync: [
    'sync.started',
    'sync.preflight_failed',
    'sync.state_changed',
    'sync.request_failed',
    'sync.rate_limited',
    'sync.paused',
    'sync.resumed',
    'sync.retry_failed_parts',
    'sync.cancelled',
    'sync.completed',
    'sync.partial',
    'sync.failed',
    'sync.interrupted',
    'lease.acquired',
    'lease.denied',
    'lease.released',
  ],
  source: [
    'source.inclusion_changed',
    'source.irrelevance_changed',
    'source.classification_changed',
    'source.remediation_changed',
    'source.batch_marked_custom_ask',
    'source.rule_proposed',
    'source.exported',
  ],
  template: [
    'template.draft_created',
    'template.updated',
    'template.validated',
    'template.previewed',
    'template.review_submitted',
    'template.published',
    'template.rolled_back',
    'template.rollout_changed',
    // added by server: release-level events (one per release), distinct from per-template publication
    'content.release_published',
    'content.release_rolled_back',
    'content.release_activated',
    'content.downloaded',
    'content.activated',
    'content.rejected',
    'content.adopted',
  ],
  artifact: [
    'artifact.previewed',
    'artifact.exported',
    'artifact.published',
    'artifact.updated',
    'artifact.repaired',
    'artifact.unlinked',
    'artifact.deleted_remote',
    'artifact.publish_failed',
  ],
  outreach: [
    'outreach.previewed',
    'outreach.drafted',
    'outreach.send_requested',
    'outreach.sent',
    'outreach.failed',
    'outreach.outcome_unknown',
    'outreach.reconciled',
    'outreach.freshness_exception',
  ],
  rumi: [
    'rumi.code_connection_prepared',
    'rumi.code_connection_configured',
    'rumi.code_connection_verified',
    'rumi.code_started',
    'rumi.code_approved',
    'rumi.code_declined',
    'rumi.code_stopped',
    'rumi.session_created',
    'rumi.session_deleted',
    'rumi.started',
    'rumi.completed',
    'rumi.partial',
    'rumi.cancelled',
    'rumi.failed',
    'rumi.usage_recorded',
    'rumi.tool_denied',
  ],
  software: [
    'software.update_checked',
    'software.update_applied',
    'software.update_rejected',
    'migration.started',
    'migration.completed',
    'migration.failed',
    'schema.migrated',
  ],
  retention: ['retention.cleanup', 'retention.storage_warning', 'backup.exported', 'backup.imported'],
  diagnostics: ['diagnostics.previewed', 'diagnostics.exported', 'audit.exported', 'usage.exported', 'audit.outbox_faulted'],
  denied: ['action.denied'],
  ops: [
    'ops.outbox_retried',
    'ops.mail_retried',
    'ops.mail_test_sent',
    'ops.release_policy_changed',
    'ops.rate_limits_changed',
    'ops.feature_flags_changed',
    'ops.model_config_changed',
    'ops.lease_released',
    'ops.usage_reconciled',
    // real-google change: Admin -> System operations -> Integrations -> Google Cloud. Details are
    // change booleans only (which fields changed, workforceSignIn); never ids, domains or secrets.
    'integration.google.updated',
    'integration.google.tested',
  ],
} as const;
export type AuditActionFamily = keyof typeof AUDIT_ACTION_FAMILIES;

export const AUDIT_ACTIONS = Object.values(AUDIT_ACTION_FAMILIES).flat() as unknown as readonly [
  AuditActionLiteral,
  ...AuditActionLiteral[],
];
type AuditActionLiteral = (typeof AUDIT_ACTION_FAMILIES)[AuditActionFamily][number];
export const AuditActionSchema = z.enum(AUDIT_ACTIONS);
export type AuditAction = z.infer<typeof AuditActionSchema>;

export function auditActionFamily(action: AuditAction): AuditActionFamily {
  for (const [family, actions] of Object.entries(AUDIT_ACTION_FAMILIES) as [AuditActionFamily, readonly string[]][]) {
    if (actions.includes(action)) return family;
  }
  return 'denied';
}

export const AUDIT_OUTCOMES = ['success', 'denied', 'failed', 'unknown'] as const;
export const AuditOutcomeSchema = z.enum(AUDIT_OUTCOMES);
export type AuditOutcome = z.infer<typeof AuditOutcomeSchema>;

export const AUDIT_TRUST_LEVELS = ['device_reported', 'server_observed', 'provider_reconciled'] as const;
export const AuditTrustLevelSchema = z.enum(AUDIT_TRUST_LEVELS);
export type AuditTrustLevel = z.infer<typeof AuditTrustLevelSchema>;

// ---------------------------------------------------------------------------------------------
// Details allowlist
// ---------------------------------------------------------------------------------------------

const UUID_SEGMENT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Endpoint template such as `/v1/tests/{testId}/entities`. Literal segments are lowercase slugs
 * or `vN`; parameters are `{name}` or `:name`. Segments that look like ids are rejected.
 */
export const EndpointTemplateSchema = z
  .string()
  .max(200)
  .regex(/^(\/(?:[a-z][a-z0-9_.-]{0,39}|v[0-9]{1,2}|\{[A-Za-z][A-Za-z0-9_]{0,39}\}|:[A-Za-z][A-Za-z0-9_]{0,39}))+(:[a-zA-Z]{1,30})?$/, 'expected an endpoint template')
  .refine(
    (v) => v.split('/').every((seg) => !UUID_SEGMENT.test(seg) && !/^[0-9]+$/.test(seg) && !/^[0-9a-f]{16,}$/i.test(seg)),
    'endpoint templates must not contain raw identifiers',
  );

/** Model / config identifiers such as `gpt-5-mini` or `price-2026-09`. */
const ConfigIdSchema = z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,63}$/);
const ShortCodeSchema = z.string().regex(/^[a-z][a-z0-9_]{0,63}$/);
const DurationMsSchema = z.number().int().nonnegative().max(7 * 24 * 60 * 60 * 1000);
const HttpStatusSchema = z.number().int().min(100).max(599);

export const AuditDetailsSchema = z
  .object({
    // ids
    accountId: AccountIdSchema.optional(),
    targetUserId: UserIdSchema.optional(),
    deviceId: DeviceIdSchema.optional(),
    sessionId: SessionIdSchema.optional(),
    invitationId: InvitationIdSchema.optional(),
    connectionId: ConnectionIdSchema.optional(),
    runId: RunIdSchema.optional(),
    snapshotId: SnapshotIdSchema.optional(),
    previousSnapshotId: SnapshotIdSchema.optional(),
    templateId: TemplateIdSchema.optional(),
    templateVersionId: TemplateVersionIdSchema.optional(),
    releaseId: ContentReleaseIdSchema.optional(),
    aiKeyId: AiKeyIdSchema.optional(),
    assignmentId: AiKeyAssignmentIdSchema.optional(),
    assignmentScope: AiKeyAssignmentScopeSchema.optional(),
    grantId: GrantIdSchema.optional(),
    artifactId: ArtifactIdSchema.optional(),
    agendaId: AgendaIdSchema.optional(),
    batchId: OutreachBatchIdSchema.optional(),
    leaseId: LeaseIdSchema.optional(),
    notificationId: NotificationIdSchema.optional(),
    rumiSessionId: RumiSessionIdSchema.optional(),
    rumiTurnId: RumiTurnIdSchema.optional(),
    relatedOperationId: OperationIdSchema.optional(),
    relatedEventId: AuditEventIdSchema.optional(),
    // enums
    provider: ProviderSchema.optional(),
    // added by server: registry provider type before/after (account.provider_changed)
    accountProvider: AccountProviderSchema.optional(),
    previousAccountProvider: AccountProviderSchema.optional(),
    // local-accounts change: account.created / account.reported / account.report_rejected details.
    // Use `accountProvider` for the account's provider type (`provider` excludes template_only).
    source: AccountOriginSchema.optional(),
    reportAction: AccountReportActionSchema.optional(),
    service: UsageServiceSchema.optional(),
    dataset: DatasetIdSchema.optional(),
    objectType: ObjectTypeSchema.optional(),
    state: SyncJobStateSchema.optional(),
    fromState: SyncJobStateSchema.optional(),
    toState: SyncJobStateSchema.optional(),
    datasetState: DatasetRunStateSchema.optional(),
    method: HttpMethodSchema.optional(),
    capability: CapabilitySchema.optional(),
    capabilities: z.array(CapabilitySchema).max(32).optional(),
    roles: z.array(RoleSchema).max(8).optional(),
    userStatus: UserStatusSchema.optional(),
    templateType: TemplateTypeSchema.optional(),
    templateState: TemplateVersionStateSchema.optional(),
    channel: ReleaseChannelSchema.optional(),
    rolloutStage: RolloutStageSchema.optional(),
    artifactType: ArtifactTypeSchema.optional(),
    publicationState: PublicationStateSchema.optional(),
    outreachMode: OutreachModeSchema.optional(),
    outreachState: OutreachItemStateSchema.optional(),
    rumiMode: RumiModeSchema.optional(),
    rumiStatus: RumiTurnStatusSchema.optional(),
    overrideScope: OverrideScopeSchema.optional(),
    errorCode: SyncErrorCodeSchema.optional(),
    remedyAction: RemedyActionSchema.optional(),
    denialReason: GrantDenialReasonSchema.optional(),
    keyStatus: AiKeyStatusSchema.optional(),
    leasePurpose: LeasePurposeSchema.optional(),
    connectionHealth: ConnectionHealthSchema.optional(),
    verificationStatus: UsageVerificationStatusSchema.optional(),
    /** Short machine code for any other reason (`revision_mismatch`, `disk_full`, ...). */
    reason: ShortCodeSchema.optional(),
    /** Which local tool was invoked/denied (Rumi tools, commands). */
    tool: ShortCodeSchema.optional(),
    command: z.string().regex(/^[a-z]+\.[a-zA-Z]+$/).optional(),
    model: ConfigIdSchema.optional(),
    priceTableVersion: ConfigIdSchema.optional(),
    contentVersion: SemVerSchema.optional(),
    fromVersion: SemVerSchema.optional(),
    toVersion: SemVerSchema.optional(),
    templateVersion: z.number().int().positive().optional(),
    policyVersion: z.number().int().positive().optional(),
    // counts
    count: CountSchema.optional(),
    recordCount: CountSchema.optional(),
    recipientCount: CountSchema.optional(),
    quarantinedCount: CountSchema.optional(),
    pageCount: CountSchema.optional(),
    pageOrdinal: CountSchema.optional(),
    requestCount: CountSchema.optional(),
    retryCount: CountSchema.optional(),
    retryAttempt: CountSchema.optional(),
    fetchedCount: CountSchema.optional(),
    addedCount: CountSchema.optional(),
    changedCount: CountSchema.optional(),
    removedCount: CountSchema.optional(),
    failedCount: CountSchema.optional(),
    skippedCount: CountSchema.optional(),
    datasetCount: CountSchema.optional(),
    citationCount: CountSchema.optional(),
    processedCount: CountSchema.optional(),
    // local-accounts change: registry reports still queued in the outbox.
    pendingCount: CountSchema.optional(),
    totalCount: CountSchema.optional(),
    inputTokens: CountSchema.optional(),
    outputTokens: CountSchema.optional(),
    cachedTokens: CountSchema.optional(),
    reasoningTokens: CountSchema.optional(),
    estimatedCostMicroUsd: MicroUsdSchema.optional(),
    budgetReservationMicroUsd: MicroUsdSchema.optional(),
    bytes: CountSchema.optional(),
    sequenceGap: CountSchema.optional(),
    // durations
    durationMs: DurationMsSchema.optional(),
    waitMs: DurationMsSchema.optional(),
    retryAfterMs: DurationMsSchema.optional(),
    elapsedMs: DurationMsSchema.optional(),
    snapshotAgeSeconds: CountSchema.optional(),
    // status codes and endpoints
    httpStatus: HttpStatusSchema.optional(),
    endpointTemplate: EndpointTemplateSchema.optional(),
    // digests and flags
    digest: Sha256HexSchema.optional(),
    outputDigest: Sha256HexSchema.optional(),
    partial: z.boolean().optional(),
    complete: z.boolean().optional(),
    revealed: z.boolean().optional(),
    staleAcknowledged: z.boolean().optional(),
    enabled: z.boolean().optional(),
    included: z.boolean().optional(),
    irrelevant: z.boolean().optional(),
    // real-google change: integration.google.updated / integration.google.tested /
    // google.identity_mismatch. Booleans only; client ids, hosted domain and secrets never appear.
    desktopClientChanged: z.boolean().optional(),
    desktopSecretChanged: z.boolean().optional(),
    webClientChanged: z.boolean().optional(),
    webSecretChanged: z.boolean().optional(),
    hostedDomainChanged: z.boolean().optional(),
    workforceSignIn: z.boolean().optional(),
    identityMismatch: z.boolean().optional(),
    // timestamps
    expiresAt: IsoDateTimeSchema.optional(),
    snapshotAt: IsoDateTimeSchema.optional(),
    nextRetryAt: IsoDateTimeSchema.optional(),
  })
  .strict();
export type AuditDetails = z.infer<typeof AuditDetailsSchema>;

/** Keys the allowlist accepts; useful for scrubbers. */
export const AUDIT_DETAIL_KEYS = Object.keys(AuditDetailsSchema.shape) as (keyof AuditDetails)[];

// ---------------------------------------------------------------------------------------------
// Event and batch
// ---------------------------------------------------------------------------------------------

export const AuditEventSchema = z
  .object({
    schemaVersion: z.literal(AUDIT_SCHEMA_VERSION),
    eventId: AuditEventIdSchema,
    occurredAt: IsoDateTimeSchema,
    /** Server receipt time; absent on device-side events until ingested. */
    receivedAt: IsoDateTimeSchema.nullable().optional(),
    /** The server overwrites this with the authenticated user on ingest. */
    actorUserId: UserIdSchema,
    deviceId: DeviceIdSchema.nullable().optional(),
    accountId: AccountIdSchema.nullable().optional(),
    action: AuditActionSchema,
    outcome: AuditOutcomeSchema,
    operationId: OperationIdSchema,
    appVersion: SemVerSchema,
    /** Monotonic per-device sequence (device events only). */
    sequence: z.number().int().nonnegative().nullable().optional(),
    /** Digest of the previous device event (sha256 hex), null for the first. */
    prevDigest: Sha256HexSchema.nullable().optional(),
    /** Digest of this event's canonical payload (set by the device or server). */
    digest: Sha256HexSchema.nullable().optional(),
    trustLevel: AuditTrustLevelSchema.optional(),
    details: AuditDetailsSchema,
  })
  .strict();
export type AuditEvent = z.infer<typeof AuditEventSchema>;

/** Fields a device fills before signing (server-only fields excluded). */
export const DeviceAuditEventSchema = AuditEventSchema.omit({ receivedAt: true, trustLevel: true }).strict();
export type DeviceAuditEvent = z.infer<typeof DeviceAuditEventSchema>;

export const AUDIT_BATCH_MAX_EVENTS = 500;

export const AuditBatchRequestSchema = z
  .object({
    deviceId: DeviceIdSchema,
    /** Sequence number of events[0]; events must be contiguous. */
    sequenceStart: z.number().int().nonnegative(),
    events: z.array(DeviceAuditEventSchema).min(1).max(AUDIT_BATCH_MAX_EVENTS),
    /**
     * Ed25519 signature (base64url) with the registered device key over the canonical JSON of
     * `{deviceId, sequenceStart, eventIds: events.map(e => e.eventId), digests: events.map(e => e.digest)}`.
     */
    signature: z.string().min(1),
  })
  .strict();
export type AuditBatchRequest = z.infer<typeof AuditBatchRequestSchema>;

export const AUDIT_REJECT_REASONS = [
  'duplicate',
  'schema_invalid',
  'sequence_conflict',
  'sequence_gap',
  'actor_mismatch',
  'account_not_assigned',
  'details_disallowed',
  'digest_mismatch',
] as const;
export const AuditRejectReasonSchema = z.enum(AUDIT_REJECT_REASONS);

export const AuditBatchResponseSchema = z.object({
  accepted: z.array(AuditEventIdSchema),
  rejected: z.array(z.object({ id: AuditEventIdSchema, reason: AuditRejectReasonSchema })),
  /** Highest contiguous sequence durably committed for this device. */
  ackSequence: z.number().int().nonnegative(),
  /** Gaps the server currently knows about for this device. */
  gaps: z.array(z.object({ from: z.number().int().nonnegative(), to: z.number().int().nonnegative() })),
});
export type AuditBatchResponse = z.infer<typeof AuditBatchResponseSchema>;

// ---------------------------------------------------------------------------------------------
// Query / export
// ---------------------------------------------------------------------------------------------

export const AuditQuerySchema = CursorQuerySchema.extend(TimeRangeQuerySchema.shape).extend({
  userId: UserIdSchema.optional(),
  accountId: AccountIdSchema.optional(),
  provider: ProviderSchema.optional(),
  deviceId: DeviceIdSchema.optional(),
  action: AuditActionSchema.optional(),
  family: z.enum(Object.keys(AUDIT_ACTION_FAMILIES) as [AuditActionFamily, ...AuditActionFamily[]]).optional(),
  outcome: AuditOutcomeSchema.optional(),
  appVersion: SemVerSchema.optional(),
  operationId: OperationIdSchema.optional(),
  trustLevel: AuditTrustLevelSchema.optional(),
  order: z.enum(['asc', 'desc']).default('desc'),
});
export type AuditQuery = z.infer<typeof AuditQuerySchema>;

export const AuditEventPageSchema = pageOf(AuditEventSchema);
export type AuditEventPage = z.infer<typeof AuditEventPageSchema>;

export const AuditEventParamsSchema = z.object({ eventId: AuditEventIdSchema });

export const AuditEventDetailResponseSchema = z.object({
  event: AuditEventSchema,
  /** Events sharing the operationId, for drill-down. */
  related: z.array(AuditEventSchema),
  actor: z.object({ id: UserIdSchema, displayName: z.string(), email: z.string() }).nullable(),
  accountName: z.string().nullable(),
});
export type AuditEventDetailResponse = z.infer<typeof AuditEventDetailResponseSchema>;

export const AuditExportRequestSchema = z
  .object({
    format: ExportFormatSchema,
    filters: AuditQuerySchema.omit({ cursor: true, limit: true }),
    timezone: z.string().min(1).max(64),
  })
  .strict();
export type AuditExportRequest = z.infer<typeof AuditExportRequestSchema>;

export const auditRoutes = {
  'audit.batches.post': defineRoute({
    id: 'audit.batches.post',
    method: 'POST',
    path: '/v1/audit/batches',
    auth: 'desktop',
    requestSchema: AuditBatchRequestSchema,
    responseSchema: AuditBatchResponseSchema,
    description: 'Ingest a signed device batch: idempotent on eventId, verifies signature, detects sequence gaps and conflicts, binds actor from session.',
  }),
  'audit.query': defineRoute({
    id: 'audit.query',
    method: 'GET',
    path: '/v1/audit/events',
    auth: 'desktop',
    capability: 'audit.read',
    requestSchema: AuditQuerySchema,
    responseSchema: AuditEventPageSchema,
    description: 'Query central audit metadata (desktop audit reviewers).',
  }),
  'audit.event.get': defineRoute({
    id: 'audit.event.get',
    method: 'GET',
    path: '/v1/audit/events/:eventId',
    auth: 'desktop',
    capability: 'audit.read',
    paramsSchema: AuditEventParamsSchema,
    requestSchema: z.object({}).loose(),
    responseSchema: AuditEventDetailResponseSchema,
    description: 'One audit event with related events.',
  }),
  'audit.export': defineRoute({
    id: 'audit.export',
    method: 'POST',
    path: '/v1/audit/exports',
    auth: 'desktop',
    capability: 'audit.export',
    requestSchema: AuditExportRequestSchema,
    responseSchema: ExportResultSchema,
    description: 'Export filtered audit metadata (CSV/JSON); records an audit.exported event.',
  }),
} as const;
